import type {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  MetaFunction,
} from "@remix-run/node";
import { json, redirect } from "@remix-run/node";
import {
  Form,
  Link,
  useActionData,
  useLoaderData,
  useNavigation,
  useOutletContext,
  useSearchParams,
} from "@remix-run/react";
import type { Locale } from "~/utils/locale";
import { detectLocale, localePath } from "~/utils/locale";
import { t } from "~/utils/translations";
import { Breadcrumbs } from "~/components/storefront/Breadcrumbs";
import { loginRetailCustomer } from "~/services/auth/retailAuth.server";
import {
  buildRetailCookieHeader,
  optionalRetailAuth,
} from "~/services/auth/retailSession.server";
import { authRateLimiter, getClientIp, buildAuthRateLimitKey } from "~/services/auth/rateLimiter.server";
import prisma from "~/utils/db.server";

export const loader = async ({ request }: LoaderFunctionArgs) => {
  const locale = detectLocale(request.url);
  const customer = await optionalRetailAuth(request);
  if (customer) {
    const redirectTo = new URL(request.url).searchParams.get("redirect");
    const dest = redirectTo && redirectTo.startsWith("/") && !redirectTo.startsWith("//") ? redirectTo : "/account";
    return redirect(localePath(dest, locale));
  }
  // Google OAuth is only shown if both env vars are deployed. This keeps the
  // "Continue with Google" button off the page on test.diolo.com until
  // the secrets are set via `flyctl secrets set`.
  const googleEnabled = Boolean(process.env.GOOGLE_CLIENT_ID && process.env.GOOGLE_CLIENT_SECRET);
  return json({ locale, googleEnabled });
};

// T3.1: auth pages excluded from search index
export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const locale = (data as { locale: Locale } | undefined)?.locale ?? "en";
  return [
    { title: t("signIn", locale) },
    { name: "robots", content: "noindex, nofollow" },
  ];
};

export const action = async ({ request }: ActionFunctionArgs) => {
  const clientIp = getClientIp(request);

  // T3.11: check a pure-IP limiter first so we can't be bypassed by an
  // attacker rotating email values. Then ALSO check a composite key
  // (ip + email hash) so a shared NAT doesn't throttle everyone after
  // one user fat-fingers their password a few times.
  const ipOnlyCheck = authRateLimiter.check(`ip-only:${clientIp}`);
  if (!ipOnlyCheck.allowed) {
    return json(
      { error: "Too many login attempts. Please try again later.", fieldErrors: {} as Record<string, string> },
      { status: 429, headers: { "Retry-After": String(Math.ceil(ipOnlyCheck.retryAfterMs / 1000)) } },
    );
  }

  const formData = await request.formData();
  const locale = (formData.get("locale") as string) || "en";
  const email = (formData.get("email") as string)?.trim();
  const password = formData.get("password") as string;
  const redirectTo = (formData.get("redirectTo") as string) || "";

  // Per-(ip, email) rate limit — applied AFTER we have the email
  const compositeCheck = authRateLimiter.check(buildAuthRateLimitKey(clientIp, email));
  if (!compositeCheck.allowed) {
    return json(
      { error: "Too many login attempts. Please try again later.", fieldErrors: {} as Record<string, string> },
      { status: 429, headers: { "Retry-After": String(Math.ceil(compositeCheck.retryAfterMs / 1000)) } },
    );
  }

  if (!email || !password) {
    return json(
      { error: t("invalidCredentials", locale as Locale), fieldErrors: {} as Record<string, string> },
      { status: 400 },
    );
  }

  const shop = await prisma.shop.findFirst();
  if (!shop) {
    return json(
      { error: "Shop configuration not found.", fieldErrors: {} as Record<string, string> },
      { status: 500 },
    );
  }

  const result = await loginRetailCustomer(email, password, shop.id);

  if ("error" in result) {
    const errorKey = result.error === "accountLocked" ? "accountLocked"
      : result.error === "accountDisabled" ? "accountDisabled"
      : "invalidCredentials";
    return json(
      { error: t(errorKey, locale as Locale), fieldErrors: {} as Record<string, string> },
      { status: result.error === "accountLocked" || result.error === "accountDisabled" ? 403 : 400 },
    );
  }

  const dest = redirectTo && redirectTo.startsWith("/") && !redirectTo.startsWith("//") ? redirectTo : "/account";
  return redirect(localePath(dest, locale as Locale), {
    headers: { "Set-Cookie": buildRetailCookieHeader(result.token) },
  });
};

export default function LoginPage() {
  const { locale } = useOutletContext<{ locale: Locale }>();
  const { googleEnabled } = useLoaderData<typeof loader>();
  const actionData = useActionData<typeof action>();
  const navigation = useNavigation();
  const [searchParams] = useSearchParams();
  const isSubmitting = navigation.state === "submitting";
  const googleUnavailable = searchParams.get("error") === "google_unavailable";

  return (
    <div className="mx-auto max-w-md px-4 py-12">
      <Breadcrumbs
        locale={locale}
        items={[
          { label: t("myAccount", locale), to: "/account" },
          { label: t("signIn", locale) },
        ]}
      />

      <h1 className="mt-8 text-base font-normal uppercase tracking-[0.1em] text-black">
        {t("signIn", locale)}
      </h1>

      {googleUnavailable && (
        <div className="mt-6 border border-[#e0e0e0] bg-[#f7f7f7] px-4 py-3">
          <p className="text-xs font-normal text-[#767676]">{t("googleUnavailable", locale)}</p>
        </div>
      )}

      {/* ── Continue with Google (only when env vars are set) ─── */}
      {googleEnabled && (
        <>
          <div className="mt-10">
            <a
              href={searchParams.get("redirect") ? `/auth/google?redirect=${encodeURIComponent(searchParams.get("redirect")!)}` : "/auth/google"}
              className="flex w-full items-center justify-center gap-3 rounded-none border border-[#e0e0e0] bg-white px-4 h-12 text-xs font-normal text-black transition-colors hover:border-black"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Continue with Google
            </a>
          </div>

          {/* ── OR separator ──────────────────────────────── */}
          <div className="mt-6 flex items-center gap-4">
            <div className="h-px flex-1 bg-gray-200" />
            <span className="text-xs uppercase tracking-widest text-luxury-stone">
              {t("or", locale)}
            </span>
            <div className="h-px flex-1 bg-gray-200" />
          </div>
        </>
      )}

      <Form method="post" noValidate className="mt-6 space-y-6">
        <input type="hidden" name="locale" value={locale} />
        <input type="hidden" name="redirectTo" value={searchParams.get("redirect") || ""} />

        {/* T2.2/T2.3: error box must have id="login-error" to match the
            aria-describedby on the email field below, and role="alert" +
            aria-live to be announced by screen readers when the error appears
            after form submission. */}
        {actionData?.error && (
          <div
            id="login-error"
            role="alert"
            aria-live="polite"
            className="border-l-2 border-red-600 pl-4"
          >
            <p className="text-xs text-red-600">{actionData.error}</p>
          </div>
        )}

        {/* Email or phone — the field keeps its `email` name so the action
            (and password managers that learned it) keep working; the auth
            service routes non-"@" identifiers to the phone lookup. */}
        <div>
          <label
            htmlFor="email"
            className="text-xs uppercase tracking-widest text-luxury-stone mb-2 block"
          >
            {locale === "vi" ? "Email hoặc số điện thoại" : "Email or phone"}
          </label>
          <input
            id="email"
            name="email"
            type="text"
            required
            autoComplete="username"
            inputMode="email"
            className="sf-input w-full"
            aria-invalid={actionData?.error ? true : undefined}
            aria-describedby={actionData?.error ? "login-error" : undefined}
          />
        </div>

        {/* Password */}
        <div>
          <label
            htmlFor="password"
            className="text-xs uppercase tracking-widest text-luxury-stone mb-2 block"
          >
            Password
          </label>
          <input
            id="password"
            name="password"
            type="password"
            required
            autoComplete="current-password"
            className="sf-input w-full"
            aria-invalid={actionData?.error ? true : undefined}
            aria-describedby={actionData?.error ? "login-error" : undefined}
          />
        </div>

        <div className="flex items-center justify-end">
          <Link
            to={localePath("/account/forgot-password", locale)}
            className="text-xs uppercase tracking-widest text-luxury-stone hover:text-luxury-charcoal transition-colors"
          >
            {t("forgotPassword", locale)}
          </Link>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="sf-btn-filled w-full !px-6 !py-3"
        >
          {isSubmitting ? "..." : t("signIn", locale)}
        </button>

        <p className="text-center text-xs text-luxury-stone">
          {t("createAccount", locale)}{" "}
          <Link
            to={localePath("/account/register", locale)}
            className="text-luxury-charcoal underline hover:text-luxury-black transition-colors"
          >
            {t("signUp", locale)}
          </Link>
        </p>
      </Form>
    </div>
  );
}

export function ErrorBoundary() {
  return (
    <div className="mx-auto max-w-md px-4 py-20 text-center">
      <h1 className="text-base font-normal uppercase tracking-[0.1em] text-black mb-4">Something went wrong</h1>
      <p className="text-luxury-stone text-sm mb-8">Please try again.</p>
      <a href="/" className="sf-btn-primary inline-block">Return Home</a>
    </div>
  );
}
